import { default as Vuex, Module, ActionContext } from "vuex";
//import uploadRollCall from "@/helpers/upload-roll-call";
//import uploadDispatch from '@/helpers/upload-dispatch';
import * as DataModels from "@gigalot/data-models";
import { UploadableScanningResult, HospitalResult, Mortality } from "@/models/uploadable";
import { uploadRollCall, uploadDispatchToAbattoir, uploadGroupWeigh, uploadHospitalResult, uploadMortality, uploadHeadCount, uploadGroupTreatment, uploadGroupVaccination } from "@/helpers/upload-helpers";
import { ObjectStoreNames } from "@/data-manager";

const uploadableObjectStores: ObjectStoreNames[] = [
  "saved-roll-calls",
  "saved-kraal-dispatches",
  "saved-abattoir-dispatches",
  "saved-hospital-results",
  "saved-mortalities",
  "saved-group-weighs",
  "saved-head-counts",
  "saved-group-treatments",
  "saved-group-vaccinations",
  "saved-crib-scores"
];

type UploadableUploader = (item: UploadableScanningResult, context: ActionContext<UploadState, any>) => Promise<void>;

async function uploadPendingScanningResults(
  context: ActionContext<UploadState, any>,
  label: string,
  dataAction: string,
  uploader: UploadableUploader
) {
  console.log(label);
  const items: UploadableScanningResult[] = await context.dispatch(dataAction, null, { root: true });
  const toUpload = items.filter(item => !item.uploaded);

  for (const item of toUpload) {
    await uploader(item, context);
    context.commit("numYetToUpload", "decrement");
  }
}

export class UploadState {
  isBusyUploading: boolean = false;
  numYetToUpload: number = 0;
  numOnDevice: number = 0;
  uploadError: boolean = false;
  uploadErrorMessages: string[] = []
}

class UploadModule implements Module<UploadState, any> {
  namespaced = true;
  state: UploadState = new UploadState();
  mutations = {
    setIsBusyUploading(state: UploadState, payload: boolean) {
      state.isBusyUploading = payload;
    },
    numYetToUpload(state: UploadState, payload: number | "increment" | "decrement") {
      if (payload === "increment") state.numYetToUpload++;
      else if (payload === "decrement") state.numYetToUpload--;
      else state.numYetToUpload = payload;
    },
    numOnDevice(state: UploadState, payload: number) {
      state.numOnDevice = payload;
    },
    uploadError(state: UploadState, payload: boolean) {
      state.uploadError = payload;
    },
    uploadErrorMessages(state: UploadState, payload: string[] | string) {
      if (typeof payload === "string") {
        state.uploadErrorMessages.push(payload);
      } else {
        state.uploadErrorMessages = payload;
      }
    }
  };
  actions = {
    async upload(context: ActionContext<UploadState, any>) {
      //let jwt = this.$store.state.user.accessToken;

      console.log("upload");
      if (context.state.isBusyUploading) {
        console.warn("Already busy uploading.");
        return;
      }
      context.commit("setIsBusyUploading", true);
      context.commit("uploadErrorMessages", [])
      try {
        await context.dispatch("uploadRollCalls");
        await context.dispatch("uploadHeadCounts");
        //await context.dispatch("uploadDispatchesToKraals");
        await context.dispatch("uploadDispatchesToAbattoirs");
        await context.dispatch("uploadGroupWeighs");
        await context.dispatch("uploadGroupTreatments");
        await context.dispatch("uploadGroupVaccinations");
        await context.dispatch("uploadHospitalResults");
        await context.dispatch("uploadMortalities");
        context.commit("uploadError", false);
      } catch (error) {
        //TODO: error log (maybe add to some messages view)
        context.commit("uploadError", true);
        context.commit("uploadErrorMessages", error)
      } finally {
        context.commit("setIsBusyUploading", false);
      }
    },
    async uploadRollCalls(context: ActionContext<UploadState, any>) {
      await uploadPendingScanningResults(context, "uploadRollCalls", "data/getRollCalls", uploadRollCall);
    },
    async uploadHeadCounts(context: ActionContext<UploadState, any>) {
      await uploadPendingScanningResults(context, "uploadHeadCounts", "data/getHeadCounts", uploadHeadCount);
    },
    async uploadHospitalResults(context: ActionContext<UploadState, any>) {
      console.log("uploadHospitalResults");
      let hospitalResults: HospitalResult[];
      //this.refreshing = true;
      hospitalResults = await context.dispatch("data/getHospitalResults", null, { root: true });
      let hospitalResultsToUpload = hospitalResults.filter(hospital => !hospital.uploaded);

      for (let i = 0; i < hospitalResultsToUpload.length; ++i) {
        await uploadHospitalResult(hospitalResultsToUpload[i], context);
        context.commit("numYetToUpload", "decrement");
      }
    },
    async uploadMortalities(context: ActionContext<UploadState, any>) {
      console.log("uploadMortalities");
      let mortalities: Mortality[];
      mortalities = await context.dispatch("data/getMortalities", null, { root: true });
      let mortalitiesToUpload = mortalities.filter((mortality) => !mortality.uploaded);

      for (let i = 0; i < mortalitiesToUpload.length; ++i) {
        await uploadMortality(mortalitiesToUpload[i], context);
        context.commit("numYetToUpload", "decrement");
      }
    },
    // async uploadDispatchesToKraals(context: ActionContext<UploadState, any>) {
    //   console.log("uploadDispatchesToKraals");
    //   let dispatchesToKraals: UploadableScanningResult[];
    //   //this.refreshing = true;
    //   dispatchesToKraals = await context.dispatch("data/getDispatchesToKraals", null, { root: true });
    //   let toUpload = dispatchesToKraals.filter(dispatchToKraal => !dispatchToKraal.uploaded);

    //   for (let i = 0; i < toUpload.length; ++i) {
    //     await uploadDispatchToKraal(toUpload[i], context);
    //   }
    // },
    async uploadDispatchesToAbattoirs(context: ActionContext<UploadState, any>) {
      await uploadPendingScanningResults(context, "uploadDispatchesToAbattoirs", "data/getDispatchesToAbattoirs", uploadDispatchToAbattoir);
    },
    async uploadGroupWeighs(context: ActionContext<UploadState, any>) {
      await uploadPendingScanningResults(context, "uploadGroupWeighs", "data/getGroupWeighs", uploadGroupWeigh);
    },
    async uploadGroupTreatments(context: ActionContext<UploadState, any>) {
      await uploadPendingScanningResults(context, "uploadGroupTreatments", "data/getGroupTreatments", uploadGroupTreatment);
    },
    async uploadGroupVaccinations(context: ActionContext<UploadState, any>) {
      await uploadPendingScanningResults(context, "uploadGroupVaccinations", "data/getGroupVaccinations", uploadGroupVaccination);
    },
    async getNumYetToUpload(context: ActionContext<UploadState, any>) {
      let numYetToUpload = 0;
      const dataGetActions = [
        "getRollCalls",
        "getGroupWeighs",
        "getDispatchesToAbattoirs",
        "getGroupTreatments",
        "getGroupVaccinations",
        "getHospitalResults",
        "getMortalities",
        "getHeadCounts"
      ];
      for (const dataGetAction of dataGetActions) {
        numYetToUpload += (await context.dispatch(`data/${dataGetAction}`, null, { root: true })).filter((i: any) => !i.uploaded).length;
      }
      context.commit("numYetToUpload", numYetToUpload);
      return numYetToUpload;
    },
    async getNumOnDevice(context: ActionContext<UploadState, any>) {
      let numOnDevice = 0;
      for (const objectStoreName of uploadableObjectStores) {
        numOnDevice += await context.dispatch("data/getObjectStoreCount", objectStoreName, { root: true });
      }
      context.commit("numOnDevice", numOnDevice);
      return numOnDevice;
    }
  };
  getters = {
    getUpstreamMetadata(state: UploadState, getters: any, rootState: any, rootGetters: any) {
      return () => {
        let userName: string = rootState.user.user.displayName;
        let userId: string = rootState.user.user.uid;
        let userEmail: string = rootState.user.user.email;
        let feedlot: string = "";
        let gcp: string = rootState.user.location ? rootState.user.location.gcp : "";
        let location: string = rootState.user.location ? rootState.user.location.guid : "";

        let metadata: DataModels.UpstreamMetadata = new DataModels.UpstreamMetadata(userName, userId, userEmail, feedlot, gcp, location);
        return metadata;
      };
    }
  };
}

const upload: UploadModule = new UploadModule();

export default upload;
